// src/pages/customers/ViewCustomers.tsx
import { useState } from "react";
import { useNavigate } from "react-router-dom";

import AdminLayout from "@/layouts/AdminLayout";
import { FiPlus } from "react-icons/fi";
import { getAuth } from "@/api/client";

import Breadcrumbs from "@/components/shared/nav/Breadcrumbs";
import ConfirmDialog from "@/components/shared/ui/ConfirmDialog";
import Input from "@/components/shared/ui/Input";

import CustomerTable, {
  type CustomerRow,
} from "@/components/customers/CustomerTable";

import { useCustomersPage } from "./useCustomersPage";

/* ===========================================================================
 * View Customers Page — uses centralized useCustomersPage hook
 * =========================================================================== */
export default function ViewCustomers() {
  /* ------------------------ State from hook ------------------------------- */
  const page = useCustomersPage();
  const navigate = useNavigate();
  const authUser = getAuth().user as any;
  const isAffiliate = String(authUser?.roleName ?? "").trim() === "Affiliate Partner";

  /* ------------------------ Local confirm dialog state -------------------- */
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmTitle, setConfirmTitle] = useState("Confirm");
  const [confirmMsg, setConfirmMsg] = useState("");
  const [confirmCta, setConfirmCta] = useState("Confirm");
  const [confirmAction, setConfirmAction] = useState<() => void>(
    () => () => {}
  );

  /* ----------------------------- Handlers --------------------------------- */

  const askDelete = (row: CustomerRow) => {
    setConfirmTitle("Delete customer");
    setConfirmMsg(
      `Do you want to delete "${row.firstName} ${row.lastName}"? This action cannot be undone.`
    );
    setConfirmCta("Delete");

    setConfirmAction(
      () =>
        () =>
          page.onDelete(row.id, {
            onSuccess: () => setConfirmOpen(false),
          })
    );

    setConfirmOpen(true);
  };

  const handleEdit = (row: CustomerRow) => {
    // Navigate to dedicated edit page
    navigate(`/customers/${row.id}/edit`);
  };

  const handleCreateClick = () => {
    // Navigate to dedicated create page
    navigate("/customers/new");
  };

  /* ------------------------------- UI ------------------------------------- */

  return (
    <AdminLayout>
      {/* -------------------------------- Header ---------------------------- */}
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Customers</h1>
          <div className="mt-1">
            <Breadcrumbs />
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {/* Global Search Box */}
          <div className="relative">
            <svg
              className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
              viewBox="0 0 24 24"
              width="16"
              height="16"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <Input
              value={page.globalQ}
              onChange={(e) => page.setGlobalQ(e.target.value)}
              placeholder="Search customers…"
              className="w-56 pl-8"
            />
          </div>

          {/* Add Customer button */}
          {!isAffiliate ? (
            <button
              className="inline-flex items-center justify-center gap-2 rounded-lg bg-sky-600 px-3 py-2 text-sm font-semibold text-white hover:bg-sky-700 disabled:opacity-50"
              onClick={handleCreateClick}
            >
              <FiPlus /> Add Customer
            </button>
          ) : null}
        </div>
      </div>

      {/* -------------------------------- Table ----------------------------- */}
      <CustomerTable
        rows={page.pageRows}
        pageRows={page.pageRows}
        serialStart={page.serialStart}
        filters={page.filters}
        setFilters={page.setFilters}
        onEdit={handleEdit}
        onDelete={askDelete}
        loading={page.customersLoading}
      />

      {/* ------------------------------- Pagination -------------------------- */}
      {/* No top margin: this bar is the foot of the table card above it. */}
      <div className="flex items-center justify-between gap-2 rounded-b-2xl border border-t-0 border-gray-200 bg-white px-4 py-3">
        <div className="text-xs text-gray-500">
          Showing{" "}
          <span className="font-medium text-gray-700">
            {page.totalFiltered === 0 ? 0 : page.start + 1}
          </span>{" "}
          to{" "}
          <span className="font-medium text-gray-700">
            {Math.min(page.start + page.pageSize, page.totalFiltered)}
          </span>{" "}
          of{" "}
          <span className="font-medium text-gray-700">
            {page.totalFiltered}
          </span>{" "}
          results
        </div>

        <div className="flex items-center gap-1">
          <button
            onClick={() => page.setPage((p) => Math.max(1, p - 1))}
            disabled={page.page === 1}
            className="inline-flex items-center gap-1 rounded-md border border-gray-200 bg-white px-2.5 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            Prev
          </button>

          <div className="hidden items-center gap-1 sm:flex">
            {Array.from({ length: Math.min(page.totalPages, 6) }).map(
              (_, idx) => {
                const n = idx + 1;
                const active = n === page.page;
                return (
                  <button
                    key={n}
                    onClick={() => page.setPage(n)}
                    className={`rounded-md px-2.5 py-1.5 text-sm ${
                      active
                        ? "bg-sky-600 text-white"
                        : "border border-gray-200 bg-white text-gray-700 hover:bg-gray-50"
                    }`}
                  >
                    {n}
                  </button>
                );
              }
            )}

            {page.totalPages > 6 && (
              <span className="px-1 text-sm text-gray-500">…</span>
            )}
          </div>

          <button
            onClick={() =>
              page.setPage((p) => Math.min(page.totalPages, p + 1))
            }
            disabled={page.page === page.totalPages}
            className="inline-flex items-center gap-1 rounded-md border border-gray-200 bg-white px-2.5 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            Next
          </button>
        </div>
      </div>

      {/* ---------------------------- Confirm Dialog ------------------------- */}
      <ConfirmDialog
        open={confirmOpen}
        title={confirmTitle}
        message={confirmMsg}
        confirmText={confirmCta}
        cancelText="Cancel"
        onCancel={() => setConfirmOpen(false)}
        onConfirm={confirmAction}
      />
    </AdminLayout>
  );
}
